// Territorios para la "conquista" de rutas del mapa en vivo.
//
// Cada territorio es un corredor que Viatik lanzó. Se define con paradas
// { name, lat, lng, zone }:
//   - zone 'a' / 'b': los dos extremos del corredor.
//   - zone 'mid':     localidades intermedias.
// Un viaje suma al territorio si sus dos puntas caen (a menos de MARGIN_KM) en
// paradas de zonas distintas: a↔b, a↔mid o mid↔b. Así un viaje dentro de un
// mismo extremo (CABA→La Plata, Montevideo→Ciudad de la Costa) no cuenta.
// Si un viaje encaja en más de un territorio gana el primero de la lista.
// Las keys siguen los nombres de rutas del backend (ARGENTINA_UNBLOCKED_ROUTES /
// URUGUAY_ROUTES); 'noa-tuc' agrupa salta-tuc y jujuy-tuc.
//
// `paths` son los trazos que se pintan, por nombre de parada. Si no se define,
// se pinta un único trazo con todas las paradas en orden. `flags` son las
// ciudades donde se planta bandera al conquistar (por defecto, las puntas del
// primer trazo).
//
// `annualAvg`: asientos vendidos por mes en promedio en los últimos 12 meses,
// medido en la base de prod al ANNUAL_AVG_AS_OF. /admin/lobbies sólo trae 2
// semanas, así que este número fijo funciona de piso para que una ruta de
// temporada (La Costa, el Este) no "pierda" el territorio en temporada baja.
// Actualizarlo a mano de vez en cuando; para rutas nuevas dejarlo en 0.

export const MARGIN_KM = 30
// Ritmo (asientos vendidos por mes) a partir del cual una ruta está dominada.
export const DOMINATION_SEATS_PER_MONTH = 500
export const ANNUAL_AVG_AS_OF = 'oct 2026'

const MVD = { name: 'Montevideo', lat: -34.9011, lng: -56.1645 }
const CABA = { name: 'CABA', lat: -34.6037, lng: -58.3816 }
const PILAR = { name: 'Pilar', lat: -34.4587, lng: -58.9142 }
const SALTA = { name: 'Salta', lat: -24.7829, lng: -65.4232 }
const GUEMES = { name: 'Gral. Güemes', lat: -24.667, lng: -65.0502 }
const PERICO = { name: 'Perico', lat: -24.3848, lng: -65.1206 }
const JUJUY = { name: 'San Salvador de Jujuy', lat: -24.1858, lng: -65.2995 }
const TUCUMAN = { name: 'San Miguel de Tucumán', lat: -26.8083, lng: -65.2176 }

export const TERRITORIES = [
  {
    key: 'salta-jujuy',
    label: 'Salta ↔ Jujuy',
    short: 'SAL–JUJ',
    annualAvg: 535,
    flags: ['Salta', 'San Salvador de Jujuy'],
    stops: [
      { ...SALTA, zone: 'a' },
      { ...GUEMES, zone: 'mid' },
      { ...PERICO, zone: 'mid' },
      { ...JUJUY, zone: 'b' },
      { name: 'Purmamarca', lat: -23.7449, lng: -65.5038, zone: 'b' }
    ]
  },
  {
    key: 'sde-tuc',
    label: 'Santiago ↔ Tucumán',
    short: 'SDE–TUC',
    annualAvg: 411,
    stops: [
      { name: 'Santiago del Estero', lat: -27.7951, lng: -64.2615, zone: 'a' },
      { name: 'Termas de Río Hondo', lat: -27.4934, lng: -64.8597, zone: 'mid' },
      { ...TUCUMAN, zone: 'b' }
    ]
  },
  {
    key: 'el-este',
    label: 'Montevideo ↔ Punta del Este',
    short: 'MVD–Este',
    annualAvg: 934,
    flags: ['Montevideo', 'Punta del Este'],
    stops: [
      { ...MVD, zone: 'a' },
      { name: 'Ciudad de la Costa', lat: -34.8233, lng: -55.9417, zone: 'a' },
      { name: 'Atlántida', lat: -34.772, lng: -55.758, zone: 'mid' },
      { name: 'Piriápolis', lat: -34.864, lng: -55.2745, zone: 'b' },
      { name: 'Maldonado', lat: -34.9087, lng: -54.958, zone: 'b' },
      { name: 'Punta del Este', lat: -34.962, lng: -54.945, zone: 'b' },
      { name: 'José Ignacio', lat: -34.844, lng: -54.636, zone: 'b' },
      { name: 'La Paloma', lat: -34.6625, lng: -54.164, zone: 'b' }
    ],
    paths: [['Montevideo', 'Ciudad de la Costa', 'Atlántida', 'Piriápolis', 'Punta del Este', 'José Ignacio', 'La Paloma']]
  },
  {
    key: 'la-costa',
    label: 'Buenos Aires ↔ La Costa',
    short: 'BA–Costa',
    annualAvg: 750,
    flags: ['CABA', 'Mar del Plata'],
    stops: [
      { ...CABA, zone: 'a' },
      { ...PILAR, zone: 'a' },
      { name: 'La Plata', lat: -34.9214, lng: -57.9545, zone: 'a' },
      { name: 'Chascomús', lat: -35.5752, lng: -58.0089, zone: 'mid' },
      { name: 'Dolores', lat: -36.3132, lng: -57.6792, zone: 'mid' },
      { name: 'Maipú', lat: -36.865, lng: -57.8826, zone: 'mid' },
      { name: 'San Clemente del Tuyú', lat: -36.3567, lng: -56.7231, zone: 'b' },
      { name: 'Mar de Ajó', lat: -36.7239, lng: -56.6772, zone: 'b' },
      { name: 'Pinamar', lat: -37.1078, lng: -56.8614, zone: 'b' },
      { name: 'Villa Gesell', lat: -37.2633, lng: -56.9731, zone: 'b' },
      { name: 'Mar del Plata', lat: -38.0055, lng: -57.5426, zone: 'b' },
      { name: 'Miramar', lat: -38.271, lng: -57.839, zone: 'b' }
    ],
    paths: [
      ['CABA', 'La Plata', 'Chascomús', 'Dolores', 'Maipú', 'Mar del Plata', 'Miramar'],
      ['Dolores', 'San Clemente del Tuyú', 'Mar de Ajó', 'Pinamar', 'Villa Gesell', 'Mar del Plata']
    ]
  },
  {
    key: 'rosario',
    label: 'Buenos Aires ↔ Rosario',
    short: 'BA–ROS',
    annualAvg: 248,
    stops: [
      { ...CABA, zone: 'a' },
      { ...PILAR, zone: 'a' },
      { name: 'Zárate', lat: -34.0981, lng: -59.0286, zone: 'mid' },
      { name: 'San Pedro', lat: -33.679, lng: -59.6663, zone: 'mid' },
      { name: 'San Nicolás', lat: -33.3342, lng: -60.2108, zone: 'mid' },
      { name: 'Rosario', lat: -32.9468, lng: -60.6393, zone: 'b' }
    ],
    paths: [['CABA', 'Zárate', 'San Pedro', 'San Nicolás', 'Rosario']]
  },
  {
    key: 'noa-tuc',
    label: 'Salta/Jujuy ↔ Tucumán',
    short: 'NOA–TUC',
    annualAvg: 0,
    stops: [
      { ...JUJUY, zone: 'a' },
      { ...SALTA, zone: 'a' },
      { ...PERICO, zone: 'mid' },
      { ...GUEMES, zone: 'mid' },
      { name: 'Metán', lat: -25.4966, lng: -64.9757, zone: 'mid' },
      { name: 'Rosario de la Frontera', lat: -25.797, lng: -64.9706, zone: 'mid' },
      { ...TUCUMAN, zone: 'b' }
    ],
    paths: [
      ['Salta', 'Gral. Güemes', 'Metán', 'Rosario de la Frontera', 'San Miguel de Tucumán'],
      ['San Salvador de Jujuy', 'Perico', 'Gral. Güemes']
    ]
  },
  {
    key: 'rc-cba',
    label: 'Río Cuarto ↔ Córdoba',
    short: 'RCU–CBA',
    annualAvg: 0,
    stops: [
      { name: 'Río Cuarto', lat: -33.1232, lng: -64.3493, zone: 'a' },
      { name: 'Elena', lat: -32.7551, lng: -64.339, zone: 'mid' },
      { name: 'Berrotarán', lat: -32.4508, lng: -64.3891, zone: 'mid' },
      { name: 'Almafuerte', lat: -32.1925, lng: -64.2556, zone: 'mid' },
      { name: 'Córdoba', lat: -31.4201, lng: -64.1888, zone: 'b' }
    ]
  },
  {
    key: 'mza-sj',
    label: 'Mendoza ↔ San Juan',
    short: 'MDZ–SJA',
    annualAvg: 0,
    stops: [
      { name: 'Mendoza', lat: -32.8895, lng: -68.8458, zone: 'a' },
      { name: 'Jocolí', lat: -32.585, lng: -68.674, zone: 'mid' },
      { name: 'Media Agua', lat: -31.9806, lng: -68.4247, zone: 'mid' },
      { name: 'San Juan', lat: -31.5375, lng: -68.5364, zone: 'b' }
    ]
  }
]
